
import {defineComponent, onMounted, reactive, ref} from 'vue';
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {ClientManager} from "@/singletons/ClientManager";
import {DocumentClassSetting} from "@dex/squeeze-client-ts";
import {ToastManager} from "@/util/ToastManager";
import DocumentClassAttributes from "@/apps/administration/components/documentclasses/DocumentClassAttributes.vue";
import DocumentClassAttributesForm from "@/apps/administration/components/documentclasses/DocumentClassAttributesForm.vue";
import DialogDelete from "@/components/DialogDelete.vue";
import EntryDialog from "@/components/EntryDialog.vue";
import BlockUI from 'primevue/blockui';

export default defineComponent({
	name: 'DocumentClassAttributesSubView',
	components: {
		BlockUI,
		EntryDialog,
		DialogDelete,
		DocumentClassAttributesForm,
		DocumentClassAttributes,
	},
	props: {
		documentClassId: {
			type: Number,
			default: 0,
		},
	},
	setup(props) {
		const {t} = useI18n();
		const toast = useToast();

		/** Document-Classes-Api */
		const documentClassApi = ClientManager.getInstance().squeeze.documentClass;

		/** List of all available Settings */
		const allDocumentClassSettings = ref<DocumentClassSetting[]>([]);

		/** List of all attributes */
		const attributes = ref<DocumentClassSetting[]>([]);

		/** Currently active attribute */
		const documentClassSetting = reactive<DocumentClassSetting>({});

		/** Show loading in table? */
		const loading = ref<boolean>(false);

		/** Show Delete-Dialog? */
		const deleteDialog = ref<boolean>(false);

		/** Should the display-name be shown as disabled? */
		const disableSettingName = ref(false);

		/** Should the Entry-Dialog for attributes be shown? */
		const showDialog = ref<boolean>(false);

		/** Show loading in dialog? */
		const loadingDialog = ref<boolean>(false);

		/** Is the Form of the Attribute invalid? */
		const isSettingInvalid = ref(true);

		/** Show error validation-messages in form? */
		const showValidationMessage = ref(false);

		/** Is DocumentClassSetting changed? */
		const changeDocumentClassSetting = ref<boolean>(false);

		/** Text of the header in Entry-Dialog */
		const headerText = ref<string>(t("Squeeze.BatchClasses.SaveAttribute"));

		/** Triggered on update of attribute-form */
		const onUpdate = (data: DocumentClassSetting, isInvalid: boolean) => {
			isSettingInvalid.value = isInvalid;
			Object.assign(documentClassSetting, data);
		}

		/**
		 * Opens the Delete Dialog
		 * @param row Row to delete
		 */
		const openDeleteDialog = (row: any) => {
			deleteDialog.value = true;
			Object.assign(documentClassSetting, row);
		}

		/**
		 * Triggered when an entry should be shown
		 * @param row
		 * @param disableSettingNameEdit
		 */
		const onEntrySelect = (row: any, disableSettingNameEdit: boolean) => {
			disableSettingName.value = disableSettingNameEdit;
			Object.assign(documentClassSetting, row);
			showDialog.value = true;
		}

		/** Reloads the data */
		const reloadData = () => {
			loading.value = true;
			const promise1 = documentClassApi.getPossibleSettingsForDocumentClass();
			const promise2 = documentClassApi.getDocumentClassSettings(props.documentClassId);

			// Wait until promises are finished
			Promise.all([promise1, promise2]).then(values => {
				allDocumentClassSettings.value = values[0] as DocumentClassSetting[];
				attributes.value = values[1] as DocumentClassSetting[];
			}).catch(response => response.json().then ((err: { message: string }) => {
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
			})).finally(() => {
				loading.value = false;
			})
		}

		/** Saves an attribute */
		const saveAttribute = () => {
			if (isSettingInvalid.value) {
				showValidationMessage.value = true;
				return;
			}

			showValidationMessage.value = false;
			loadingDialog.value = true;
			loading.value = true;

			documentClassApi.putDocumentClassSettingByName(props.documentClassId, documentClassSetting.settingName!, documentClassSetting.value!)
				.then(() => {
					if(!changeDocumentClassSetting.value) {
						reloadData();
					}
					showDialog.value = false;
				}).catch(response => response.json().then ((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loadingDialog.value = false;
					loading.value = false;
					changeDocumentClassSetting.value = false;
				})
		}

		/** Deletes the selected entry */
		const deleteEntry = () => {
			if (!props.documentClassId && !documentClassSetting.settingName) {
				return;
			}

			loading.value = true;
			documentClassApi.deleteDocumentClassSettingByName(props.documentClassId, documentClassSetting.settingName!)
				.then(() => {
					reloadData();
				}).catch(response => response.json().then ((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				})
		}

		onMounted(() => {
			reloadData();
		});

		return {
			t,
			toast,
			allDocumentClassSettings,
			attributes,
			documentClassSetting,
			loading,
			deleteDialog,
			disableSettingName,
			showDialog,
			loadingDialog,
			isSettingInvalid,
			showValidationMessage,
			changeDocumentClassSetting,
			headerText,
			onUpdate,
			openDeleteDialog,
			onEntrySelect,
			reloadData,
			saveAttribute,
			deleteEntry,
		};
	},
});

